import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import host from "../../global.jsx";
import { toast } from "react-toastify";
import Loader from "../Loader.jsx";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";

import DiscardChangesModal from "../shared/DiscardChangesModal";
import SetDetailsCard, { DEFAULT_CONFIG } from "./mcq/manual/SetDetailsCard";
import QuestionListPanel from "./mcq/manual/QuestionListPanel";
import BuilderSidebar from "./mcq/manual/BuilderSidebar";

const DRAFT_KEY = "mcq_set_draft";

const emptyDraft = () => ({
  name: "",
  description: "",
  questions: [],
  config: { ...DEFAULT_CONFIG },
});

/** Blocking rules, mirroring the backend's save validation. */
const validateDraft = (draft) => {
  const errors = {};
  draft.questions.forEach((q) => {
    const texts = q.options.map((o) => (o.text || "").trim());
    if (!(q.text || "").trim()) errors[q.id] = "Question text is required";
    else if (q.options.length < 2) errors[q.id] = "Needs at least 2 options";
    else if (texts.some((t) => !t)) errors[q.id] = "Every option needs text";
    else if (new Set(texts.map((t) => t.toLowerCase())).size !== texts.length)
      errors[q.id] = "Options must be distinct";
    else if (!q.options.some((o) => o.id === q.correct_option_id))
      errors[q.id] = "Mark the correct answer";
  });
  return errors;
};

/**
 * Builds one MCQ set — new when there is no :setId, otherwise the existing one.
 * Browsing sets lives on the library page; this page only ever holds one set, so
 * the details, the questions and the readiness rail are the whole surface.
 *
 * The whole set is a single client-side draft saved in one call, so a half-built
 * set is never reachable from a JD.
 */
const CreateMCQSet = () => {
  const navigate = useNavigate();
  const { setId } = useParams();
  const accessToken = sessionStorage.getItem("accessToken");
  const authHeaders = { Authorization: `Bearer ${accessToken}` };

  const [sets, setSets] = useState([]);
  const [draft, setDraft] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const [showImport, setShowImport] = useState(false);
  // Errors exist from the first keystroke, but flashing them immediately reads as
  // the form accusing the user before they've done anything. Stay quiet until the
  // first Create Set / Save Changes attempt, then validate live from there on.
  const [attemptedSave, setAttemptedSave] = useState(false);

  // Names of other sets, for the duplicate warning only — never for binding.
  const fetchSets = async () => {
    try {
      const res = await axios.get(`${host}/mcq-sets`, { headers: authHeaders });
      setSets(res.data.sets || []);
    } catch {
      /* the warning is a nicety; failing to load names must not block editing */
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      await fetchSets();

      let loaded;
      if (!setId) {
        loaded = emptyDraft();
      } else {
        try {
          const res = await axios.get(`${host}/mcq-sets/${setId}`, { headers: authHeaders });
          const doc = res.data;
          loaded = {
            name: doc.name || "",
            description: doc.description || "",
            questions: doc.questions || [],
            config: { ...DEFAULT_CONFIG, ...(doc.config || {}) },
          };
        } catch {
          if (!cancelled) {
            toast.error("Could not load that set");
            navigate("/org-admin/mcqsets");
          }
          return;
        }
      }
      if (cancelled) return;

      // Refresh recovery happens here rather than in its own effect: the persist
      // effect below also runs once loading ends, and would otherwise overwrite the
      // stored draft with the server copy before it could ever be read back.
      // The stored id must match the set being edited, or one set's unsaved work
      // would restore itself over a different set.
      let recovered = false;
      try {
        const saved = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null");
        if (saved?.draft && (saved.setId ?? null) === (setId ?? null)) {
          loaded = saved.draft;
          recovered = true;
        }
      } catch {
        sessionStorage.removeItem(DRAFT_KEY);
      }

      setDraft(loaded);
      setIsDirty(recovered);
      setAttemptedSave(false);
      setIsLoading(false);
      if (recovered) toast.info("Recovered your unsaved set");
    };

    load();
    return () => { cancelled = true; };
  }, [setId]);

  // Only unsaved work is worth storing — persisting a pristine draft would make an
  // untouched visit to /new look like recovered work on the next load.
  useEffect(() => {
    if (!draft || isLoading || !isDirty) return;
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ setId: setId ?? null, draft }));
  }, [draft, setId, isLoading, isDirty]);

  const handleDraftChange = (next) => {
    setDraft(next);
    setIsDirty(true);
  };

  const questionErrors = useMemo(() => (draft ? validateDraft(draft) : {}), [draft]);
  // Gated copy handed to the question list and sidebar — real errors exist as soon
  // as validateDraft runs, but nothing shows them until attemptedSave flips.
  const visibleErrors = attemptedSave ? questionErrors : {};

  const nameWarning = useMemo(() => {
    if (!draft?.name?.trim()) return "";
    const clash = sets.some(
      (s) => s.id !== setId &&
        s.name.trim().toLowerCase() === draft.name.trim().toLowerCase()
    );
    return clash ? "Another set already uses this name" : "";
  }, [draft, sets, setId]);

  const jumpToQuestion = (questionId) => {
    setExpandedId(questionId);
    // Let the accordion paint before scrolling, or the target is still collapsed.
    requestAnimationFrame(() => {
      document
        .getElementById(`mcq-q-${questionId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleSave = async () => {
    if (!draft) return;
    // First attempt is what turns on live error display — see `attemptedSave`.
    setAttemptedSave(true);
    if (!draft.name.trim()) { toast.error("Set name is required"); return; }
    if (!draft.questions.length) { toast.error("Add at least one question"); return; }
    const errorCount = Object.keys(questionErrors).length;
    if (errorCount) {
      toast.error("Some fixes required");
      return;
    }

    setIsSaving(true);
    try {
      // Whole-document write — no per-question endpoints, so no positional index
      // can go stale between two admins editing the same set.
      if (setId) {
        await axios.put(`${host}/mcq-sets/${setId}`, draft, {
          headers: { ...authHeaders, "Content-Type": "application/json" },
        });
        toast.success("Set saved");
      } else {
        await axios.post(`${host}/mcq-sets`, draft, {
          headers: { ...authHeaders, "Content-Type": "application/json" },
        });
        toast.success("Set created");
      }
      sessionStorage.removeItem(DRAFT_KEY);
      setIsDirty(false);
      navigate("/org-admin/mcqsets");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error saving set");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    if (!setId) return;
    try {
      const res = await axios.delete(`${host}/mcq-sets/${setId}`, { headers: authHeaders });
      const jobs = res.data?.jobs_updated || 0;
      toast.success(
        jobs ? `Set deleted — ${jobs} job${jobs === 1 ? "" : "s"} updated` : "Set deleted"
      );
      sessionStorage.removeItem(DRAFT_KEY);
      navigate("/org-admin/mcqsets");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error deleting set");
    }
  };

  const leave = () => {
    sessionStorage.removeItem(DRAFT_KEY);
    navigate("/org-admin/mcqsets");
  };

  const handleBack = () => (isDirty ? setShowDiscard(true) : leave());

  if (isLoading || !draft) return <Loader />;

  // Only the two structural preconditions gate the button. Per-question problems
  // deliberately do not — the first click is what flips `attemptedSave` and turns
  // their display on, so disabling here would hide the errors it reveals.
  const saveBlockedReason = !draft.name.trim()
    ? "Add a set name"
    : !draft.questions.length
    ? "Add at least one question"
    : "";

  return (
    <>
      {/* h-full/min-h-full rather than min-h-screen: the scroll area is the portal's
          <main>, which is shorter than 100vh by the header, so a vh unit overshoots and
          pushes the rail's foot below the fold. */}
      <div className="h-full">
        <div className="bg-bgPrimary flex flex-col min-h-full border border-gray-700/50 rounded-2xl pt-4 pb-6">
          {/* Page header — navigation and the destructive action only. Save lives at
              the foot of the rail, next to the readiness blocks that gate it. */}
          <div className="flex items-center justify-between gap-4 px-6 pt-2 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={handleBack}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors shrink-0"
              >
                <ArrowLeft className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
              </button>
              <span className="dark:text-gray-400 text-textSecondary">
                {setId ? "Edit MCQ Set" : "New MCQ Set"}
              </span>
            </div>

            {setId && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                title="Delete set"
                className="p-2.5 rounded-full border border-white/15 dark:text-gray-400 text-textSecondary hover:text-red-400 hover:border-red-400/40 transition-colors shrink-0"
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>

          <div className="w-full flex-1 mt-4 px-6 lg:px-12 flex flex-col gap-5">
            <SetDetailsCard
              draft={draft}
              nameWarning={nameWarning}
              onDraftChange={handleDraftChange}
            />

            {/* lg:items-stretch is what makes the rail as tall as the page rather than
                as tall as its cards, so Save Set lands at the bottom-right. */}
            <div className="flex flex-1 flex-col lg:flex-row gap-6 items-start lg:items-stretch">
              <QuestionListPanel
                questions={draft.questions}
                errors={visibleErrors}
                onChange={(questions) => handleDraftChange({ ...draft, questions })}
                expandedId={expandedId}
                onExpandedChange={setExpandedId}
                showImport={showImport}
                onShowImportChange={setShowImport}
              />

              <BuilderSidebar
                questions={draft.questions}
                errors={visibleErrors}
                config={draft.config}
                onJumpTo={jumpToQuestion}
                expandedId={expandedId}
                onImportClick={() => setShowImport(true)}
                saveLabel={setId ? "Save Changes" : "Create Set"}
                saveBlockedReason={saveBlockedReason}
                isSaving={isSaving}
                isDirty={isDirty}
                onSave={handleSave}
              />
            </div>
          </div>
        </div>
      </div>

      <DiscardChangesModal
        isOpen={showDiscard}
        onCancel={() => setShowDiscard(false)}
        onConfirm={() => { setShowDiscard(false); leave(); }}
      />

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm bg-bgPrimary dark:glass-effect rounded-2xl border border-white/10 p-6 space-y-4">
            <h3 className="text-base font-semibold dark:text-white text-textPrimary">
              Delete this set?
            </h3>
            <p className="text-sm dark:text-gray-400 text-textSecondary">
              Jobs currently using it will have their MCQ round cleared. Reports from
              candidates who already took it are unaffected.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="border-btn dark:border-btn-dark flex-1 px-4 py-2.5 rounded-full text-sm font-medium transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2.5 rounded-full bg-red-500/90 hover:bg-red-500 text-white transition-colors text-sm font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateMCQSet;
